import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';

interface AdjustOrderAmountDialogProps {
  open: boolean;
  orderId: string;
  currentAmount: number;
  onConfirm: (newAmount: number) => void;
  onCancel: () => void;
  loading?: boolean;
}

const AdjustOrderAmountDialog: React.FC<AdjustOrderAmountDialogProps> = ({
  open,
  orderId,
  currentAmount,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [adjustedAmount, setAdjustedAmount] = useState<string>(currentAmount.toString());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAdjustedAmount(currentAmount.toString());
      setError(null);
    }
  }, [open, currentAmount]);

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAdjustedAmount(value);
    setError(null);

    // Validate the input
    if (value === '') {
      setError('Amount cannot be empty');
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (numValue < 0) {
      setError('Amount cannot be negative');
      return;
    }
  };

  const handleConfirm = () => {
    const numValue = parseFloat(adjustedAmount);
    
    if (isNaN(numValue) || numValue < 0) {
      setError('Please enter a valid amount');
      return;
    }

    onConfirm(numValue);
  };

  const amountDifference = parseFloat(adjustedAmount) - currentAmount;
  const hasChanged = Math.abs(amountDifference) > 0.01;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="adjust-amount-dialog-title"
    >
      <DialogTitle id="adjust-amount-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <MoneyIcon color="primary" />
          <Typography variant="h6">Adjust Order Amount</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Order ID: {orderId}
          </Typography>
          
          <Box sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Order Amount:
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
              ₹{currentAmount.toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <TextField
            autoFocus
            margin="dense"
            label="New Order Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={adjustedAmount}
            onChange={handleAmountChange}
            error={!!error}
            helperText={error || (hasChanged && (
              <Typography
                variant="caption"
                color={amountDifference > 0 ? 'error' : 'success'}
                sx={{ fontWeight: 'bold' }}
              >
                {amountDifference > 0 ? '+' : ''}₹{amountDifference.toFixed(2)} 
                {' '}({amountDifference > 0 ? 'Increase' : 'Decrease'})
              </Typography>
            ))}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
            }}
            inputProps={{
              min: 0,
              step: 0.01,
            }}
            sx={{ mt: 2 }}
          />

          {hasChanged && (
            <Box sx={{ mt: 2 }}>
              <Alert 
                severity={amountDifference > 0 ? 'warning' : 'info'}
                sx={{ mt: 1 }}
              >
                <Typography variant="body2">
                  {amountDifference > 0 
                    ? `The order amount will be increased by ₹${amountDifference.toFixed(2)}. The customer will be notified of this change.`
                    : `The order amount will be decreased by ₹${Math.abs(amountDifference).toFixed(2)}.`
                  }
                </Typography>
              </Alert>
            </Box>
          )}

          <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              New Total Amount:
            </Typography>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
              ₹{(() => {
                const amount = parseFloat(adjustedAmount);
                return (!isNaN(amount) && amount >= 0) ? amount.toFixed(2) : currentAmount.toFixed(2);
              })()}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !!error}
          color="primary"
          variant="contained"
        >
          {loading 
            ? (hasChanged ? 'Updating...' : 'Confirming...') 
            : (hasChanged ? 'Update Amount & Confirm Order' : 'Continue Without Adjusting')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdjustOrderAmountDialog;
